import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import { Observable, catchError, finalize, switchMap, take, tap, throwError } from 'rxjs';
import { AuthService } from 'src/app/components/auth/service/auth.service';
import { Router } from '@angular/router';
import { SocketService } from '../socket-service/socket-service.service';
import { SharedDataService } from '../shared-service/shared-data.service';
import * as moment from 'moment-timezone';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {

  constructor(
    private authService: AuthService,
    private router: Router,
    private socket: SocketService,
    private sharedDataService: SharedDataService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const skipUrls = [
      '/auth/refresh-tokens',
      '/auth/login'
    ];

    const isSkip = skipUrls.some(url => request.url.includes(url));

    if (isSkip) {
      return next.handle(request);
    }

    const accessToken = this.authService.getToken('access_token');

    if (accessToken) {
      request = this.addToken(request, accessToken);
    }

    if (!(request.body instanceof FormData)) {
      request = request.clone({
        headers: request.headers.set('Content-Type', 'application/json')
      });
    }

    return this.sharedDataService.getUser().pipe(
      take(1),
      switchMap(user => {
        let isTokenExpired = false;
        if (user) {
          const timeZone = user.timeZone;
          const now = moment().tz(timeZone);
          const expiry = moment(this.authService.getTokenExpiry('access_token_expiry')).tz(timeZone).subtract(5, 'minutes');
          isTokenExpired = now.isAfter(expiry);
        }

        if (user && isTokenExpired) {
          return this.handleTokenExpired(request, next);
        } else {
          return next.handle(request).pipe(
            catchError((error) => {
              // Check if access token is expired
              if (error.status === 401 && accessToken) {
                return this.handleTokenExpired(request, next);
              }

              return throwError(() => error);
            })
          );
        }
      })
    );
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`)
    });
  }

  private handleTokenExpired(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Call the refresh token endpoint to get a new access token
    return this.authService.refreshAccessToken().pipe(
      tap((response) => {
        if (response.access && response.access.token) {
          this.authService.setToken('access_token', response.access.token);
          this.authService.setTokenExpiry('access_token_expiry', response.access.expires);
          this.socket.connectSocket();
        }
        if (response.refresh && response.refresh.token) {
          this.authService.setToken('refresh_token', response.refresh.token);
        }
      }),
      switchMap(() => {
        const newAccessToken = this.authService.getToken('access_token');
        // Retry the original request with the new access token
        return next.handle(this.addToken(request, newAccessToken));
      }),
      catchError((error) => {
        // Handle refresh token error (e.g., redirect to login page)
        console.error('Error handling expired access token:', error);
        if (error.status === 401) {
          this.socket.disconnectSocket();
          this.authService.removeToken('access_token');
          this.authService.removeTokenExpiry('access_token_expiry');
          this.authService.removeToken('refresh_token');
          this.router.navigateByUrl('/login');
        }

        return throwError(() => error);
      })
    );
  }
}
